import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from "react-redux";
import PropTypes from "prop-types";

import CustomSizeList from "./customSize/CustomSizeList";
import CustomSizeSwapButton from "./customSize/CustomSizeSwapButton";
import Switcher from "../../../ui/Switcher";
import sqftShape from "../../../canvas/core/shapes/sqftShape";
import ConfirmButtons from "./ConfirmButtons";
import { is3DMode } from "../../NavigationHelper";
import {reloadCountertopsIn3DMode, reloadShapesIn3DMode} from "../../../../helpers/3dHelper/modelsHelper";
import workspaceControllers from "../../../canvas/store/workspaceControllers";
import {isShapeTallCabinet} from "../../../../helpers/cabinetBoxCompatibilityHelper";

const SectionCustomSize = (props) => {
  const { shape, refreshCabinetAttributes } = props;
  const [isEnabled, setEnabled] = useState(false);
  const [listKey, setCustomSizeListKey] = useState(0);
  const [isProcessing, setProcessing] = useState(false);
  const [hasPendingDimensionChanges, setHasPendingDimensionChanges] = useState(false);

  const getShapeDimensions = useCallback(() => ({
    width:  shape.getWidth(),
    height: shape.getHeight(),
    depth:  shape.getDepth(),
  }), [shape]);

  const initialShapeDimensionsRef = useRef(getShapeDimensions());

  useEffect(() => {
    initialShapeDimensionsRef.current = getShapeDimensions();
    setHasPendingDimensionChanges(false);
  }, [getShapeDimensions]);

  const handleDimensionsChange = () => {
    const currentDimensions = getShapeDimensions();
    const initialDimensions = initialShapeDimensionsRef.current;
    const hasChanges = Object.keys(initialDimensions).some(
      dimension => currentDimensions[dimension] !== initialDimensions[dimension]
    );

    setHasPendingDimensionChanges(hasChanges);
  };

  const onChange = () => {
    const nextIsEnabled = !isEnabled;
    if (nextIsEnabled) {
      initialShapeDimensionsRef.current = getShapeDimensions();
    }
    setHasPendingDimensionChanges(false);
    setEnabled(nextIsEnabled);
  };

  const confirmCallback = async () => {
    if (isProcessing) {
      return;
    }

    setProcessing(true);
    try {
      const snappedShapes = workspaceControllers.snappedShapeGroups.getSnappedShapesForTheShape(shape);
      await reloadShapesIn3DMode([...snappedShapes, shape]);
      await reloadCountertopsIn3DMode([...snappedShapes, shape]);
    } finally {
      setProcessing(false);
      setHasPendingDimensionChanges(false);
    }
    initialShapeDimensionsRef.current = getShapeDimensions();
    setCustomSizeListKey(prev => prev + 1);
  };

  const cancelCallback = async () => {
    if (isProcessing) {
      return;
    }

    const initialShapeDimensions = initialShapeDimensionsRef.current;
    const snappedShapes = workspaceControllers.snappedShapeGroups.getSnappedShapesForTheShape(shape);
    shape.setHeight(initialShapeDimensions.height);
    shape.setDepth(initialShapeDimensions.depth);
    shape.setWidth(initialShapeDimensions.width);

    setProcessing(true);
    try {
      await reloadShapesIn3DMode([...snappedShapes, shape]);
      await reloadCountertopsIn3DMode([...snappedShapes, shape]);
    } finally {
      setProcessing(false);
      setHasPendingDimensionChanges(false);
    }
    initialShapeDimensionsRef.current = getShapeDimensions();
    setCustomSizeListKey(prev => prev + 1);
  };

  return (
    <div className="sr__section sr__custom-size">
      <div className="sr__section-title">
        Enable Custom Size
        <span>
          <Switcher value={isEnabled} onChange={onChange} />
        </span>
      </div>

      <CustomSizeList
        listKey={listKey}
        shape={shape}
        isEnabled={isEnabled}
        refreshCabinetAttributes={refreshCabinetAttributes}
        onDimensionsChange={handleDimensionsChange}
      />
      {/* We have live resizing for the tall cabinets without using api */}
      {is3DMode() && isEnabled && hasPendingDimensionChanges && !isShapeTallCabinet(shape) && (
        <div
          style={{
            pointerEvents: isProcessing ? 'none' : 'auto',
            opacity:        isProcessing ? 0.6      : 1,
            transition:     'opacity 0.2s'
          }}
        >
          <ConfirmButtons
            confirmCallback={confirmCallback}
            cancelCallback={cancelCallback}
            isLoading={isProcessing}
          />
        </div>
      )}

      <CustomSizeSwapButton shape={shape} setCustomSizeListKey={setCustomSizeListKey}/>
    </div>
  );
};

SectionCustomSize.propTypes = {
  shape: PropTypes.instanceOf(sqftShape).isRequired,
  refreshCabinetAttributes: PropTypes.func.isRequired,
};

const mapStateToProps = (store) => ({
  menuOpened: store.sqftMenuState.menuOpened,
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(SectionCustomSize);
